/**
 * Bulk Upload — many photos into one album, in one page load.
 *
 * READ THIS BEFORE CHANGING ANYTHING IN THIS FEATURE. Each paragraph is a
 * decision that cost evidence to establish; several are traps this repo has
 * already fallen into.
 *
 * TWO-LEG, NON-ATOMIC COMMIT. Publishing one photo is two independent writes:
 * a storage PUT into the public `photos` bucket, then an INSERT into
 * public.photos. They cannot be atomic from a browser. Therefore EVERY
 * non-success INSERT outcome — error, timeout, or a missing returned id — runs
 * a compensating storage remove() for the object it just wrote. When that
 * remove() ALSO fails, the path is unrecoverable (see .list() below), so it is
 * surfaced in the summary as a copyable orphan path and console.warn'd. The
 * summary must never report a clean run while orphanPaths is non-empty.
 *
 * upsert: false IS MANDATORY. storage.objects has no SELECT policy (dropped in
 * 20260722120000_security_advisor_fixes.sql:137), so an upsert's implicit read
 * fails RLS with 42501. This has bitten the project twice. Paths carry
 * Date.now() + random, so collisions do not occur and upsert buys nothing.
 *
 * .list() IS IMPOSSIBLE for the same reason. There is no way to enumerate the
 * bucket from the admin panel to find orphans after the fact. The copyable path
 * list in the summary is the only recovery record.
 *
 * supabase-js IGNORES contentType FOR Blob/File BODIES. It builds FormData and
 * the part type comes from the Blob's own .type. Prod's bucket carries
 * allowed_mime_types = {image/*, video/*} (set out of band), so a typeless blob
 * uploads as application/octet-stream and is rejected 400 BEFORE RLS is
 * consulted. Every body handed to upload() must itself carry a correct .type;
 * contentType is passed as documentation only.
 *
 * DB INSERTS RUN AT CONCURRENCY 1. public.photos carries the admin audit
 * trigger (20260702120000_admin_audit_log.sql:311), which takes FOR UPDATE on
 * the single admin_audit_chain row for the duration of each write. The
 * `authenticated` role runs with statement_timeout = 8s and lock_timeout = 8s,
 * so parallel inserts do not merely slow a concurrent admin's save — they FAIL
 * it. Decode and storage PUT run at 3 slots; the INSERT leg is serialized
 * behind a single-permit lock. Do not "optimize" this.
 *
 * TRIPWIRE — REALTIME. public.photos is NOT in the supabase_realtime
 * publication, and mobile/src/features/gallery/usePhotos.ts is a plain
 * useQuery with no channel. A 200-row batch therefore causes no fan-out today.
 * IF A FUTURE TASK ADDS public.photos TO supabase_realtime, this feature
 * becomes a fan-out source and must be revisited (debounce on the mobile side).
 *
 * THE UPLOADS KILL SWITCH EXEMPTS ADMINS. Both RESTRICTIVE policies in
 * 20260728210932_uploads_kill_switch.sql end in `or public.is_admin()`, so this
 * page keeps working while member uploads are paused. That is deliberate (an
 * admin escape hatch), so the page SHOWS the notice and does not block.
 *
 * UPLOADS PUBLISH IMMEDIATELY AND ARE NOT SCANNED. photos.moderation_status
 * defaults to 'approved'; this page sets it explicitly for legibility in the
 * row and in the audit snapshot, and does NOT invoke the moderate-image
 * function (admin content; the integrity_image_moderation flag ships off). A
 * mis-dropped folder is live in the alumni app instantly. The page copy says so
 * above the dropzone, and the summary links straight to the album.
 *
 * NO SVG, EVER, AND NO WebView/dangerouslySetInnerHTML FOR PHOTO DATA. The
 * bucket is PUBLIC and its allowlist includes image/* — which covers
 * image/svg+xml. An SVG served from the storage origin executes script if it is
 * ever loaded in an iframe/object. The client rejects SVG outright. Narrowing
 * the bucket allowlist is deferred as `photos-bucket-mime-narrowing` because
 * the same bucket serves feed-post video.
 *
 * MASS UPLOAD IS AN ACCEPTED ADMIN-ROLE RISK. A compromised or careless admin
 * account can push 500 MB into a public bucket; admins are exempt from every
 * guard by design. Mitigations that DO exist: the per-batch caps (200 files,
 * 20 MB/photo, 8 MB/GIF, 500 MB/batch), the destination confirmation naming the
 * backend host, and full audit attribution (one admin_audit_log row per photo
 * with actor_id, actor_email and a JSONB snapshot). There is no rate limit.
 */
import React from 'react'
import { App, Button, Input, Select, Spin } from 'antd'
import { Link } from 'react-router'
import { useSelect } from '@refinedev/antd'
import { useInvalidate, useWarnAboutChange } from '@refinedev/core'
import type { Tables } from '@muaa/shared'
import { ACTIVE_BACKEND_URL, supabaseClient } from '../lib/supabaseClient'
import { brand } from '../theme/brand'
import { EmptyHint, ListPage, Pill, ProgressBar, SectionCard } from '../theme/listkit'
import { EnvBadge } from '../components/EnvSwitch'
import { BulkUploadRow } from './photoBulkUpload/BulkUploadRow'
import {
  ACCEPT_ATTR,
  MAX_FILES,
  clampBatchStart,
  clampLabel,
  partitionDroppedFiles,
  sanitizeLabel,
  thresholdWarning,
} from './photoBulkUpload/helpers'
import { hasSubtleCrypto, processFile as browserProcessFile, type ProcessFile } from './photoBulkUpload/pipeline'
import {
  createUploadQueue,
  ABORT_PERMISSION_COPY,
  PAUSE_AUTH_COPY,
  type BatchOutcome,
  type BulkRow,
  type PauseReason,
  type QueueDeps,
  type QueueHandle,
} from './photoBulkUpload/uploadQueue'
import { existingLabelWarning, resolveAlbumForStart, type AlbumDeps } from './photoBulkUpload/album'
import { BulkUploadSummary, runInvalidations } from './photoBulkUpload/summary'

type Album = Tables<'albums'>

/** Every operator-facing string, in one place so tests assert the real copy. */
export const COPY = {
  eyebrow: 'Content · Photos',
  intro:
    'Upload many photos to one album at once. Keep this tab open until the batch finishes; photos already uploaded stay uploaded. Photos are ordered by the order shown below and appear at the end of the album in the app.',
  back: 'Back to Photo Gallery',
  albumLabel: 'Album',
  albumPlaceholder: 'Choose an album',
  newAlbum: 'New album…',
  newAlbumPlaceholder: 'e.g. Homecoming 2025',
  killSwitch: 'Member uploads are currently paused. Admin uploads are exempt and will still publish.',
  notAdmin: 'Bulk upload is limited to administrators.',
  badConfig: 'This deployment is missing its Supabase configuration. Bulk upload is unavailable here.',
  moderation:
    'Uploaded photos are visible in the app immediately and are not scanned. Review them before uploading.',
  draggerPrimary: 'Drag files or a folder here',
  draggerSecondary: 'JPEG, PNG, WebP or GIF. Up to 20 MB per photo (8 MB for GIF), 200 photos per batch.',
  chooseFiles: 'Choose files',
  chooseFolder: 'Choose folder',
  /**
   * The Files card's empty state. Deliberately NOT `draggerSecondary`: that
   * exact string is already rendered in the dropzone, and two nodes with
   * identical text make every `getByText` for it ambiguous.
   */
  noFiles: 'No files added yet.',
  dedupeScope:
    'Duplicate detection applies within this batch only — photos already in the album are not detected.',
  dedupeUnavailable: 'Duplicate detection needs a secure connection — it is off for this session.',
  emptyDrop:
    'No files were read from that drop. Your browser may not expand folders — use Choose files instead.',
  emptyDropSecond: 'Drag files from your computer — images dragged from a web page cannot be uploaded.',
  concurrency: 'Large batches briefly slow other admin saves.',
  auditNote: 'Each photo is recorded individually in the Audit Trail.',
  interleave:
    'If another admin is uploading to the same album at the same time, the two batches interleave.',
  noResume: 'A reload or a closed tab ends the batch; photos already uploaded stay uploaded.',
  start: 'Start upload',
  cancel: 'Cancel',
  resume: 'Resume',
  needAlbum: 'Choose an album to start.',
  needFiles: 'Add files to start.',
  offline: 'You appear to be offline.',
  noSession: 'Your session expired. Sign in again in another tab, then press Start.',
  /** EC-68: uploaded_by was captured at Start and nothing server-side re-checks it. */
  otherAdmin: 'You are signed in as a different administrator. Start a new batch.',
  /**
   * EC-61's second half. The 5 s throttle only ever says "{n} of {N} complete",
   * and the ticker dies with the batch - so without a final write the live
   * region's last word to a screen-reader operator is a stale progress line
   * that never mentions the failures. Written once, on a terminal outcome only
   * (a pause is not the end of the batch).
   */
  finished: (done: number, failed: number, skipped: number) =>
    `Finished: ${done} uploaded, ${failed} failed, ${skipped} skipped`,
} as const

export interface PhotoBulkUploadPageProps {
  /** Injected for tests; defaults to the real browser pipeline. */
  processFile?: ProcessFile
  /**
   * Injected for tests; defaults to ACTIVE_BACKEND_URL. Same idiom as
   * EnvBadge({ url = ACTIVE_BACKEND_URL }) in components/EnvSwitch.tsx.
   */
  backendUrl?: string
}

type Guard = 'loading' | 'admin' | 'not-admin' | 'bad-backend'
type Phase = 'idle' | 'running' | 'paused' | 'terminal'

let rowSeq = 0
const nextRowId = (): string => {
  rowSeq += 1
  return `bulk-${rowSeq}-${Math.random().toString(36).slice(2, 8)}`
}

const makeRow = (file: File): BulkRow => ({
  id: nextRowId(),
  file,
  name: file.name,
  size: file.size,
  label: sanitizeLabel(file.name),
  state: 'queued',
  attempt: 0,
})

const UNKNOWN_HOST = 'unknown host'

const hostOf = (url: string): string => {
  try {
    return new URL(url).host
  } catch {
    return UNKNOWN_HOST
  }
}

/**
 * TRAP: `ACTIVE_BACKEND_URL` is `target.url` (supabaseClient.ts:53), which in a
 * misconfigured deployment is the EMPTY STRING — the `missing-tenant-config.invalid`
 * placeholder is only ever handed to createClient(), never exported. So the degraded
 * state has to be detected from an unparseable URL as well as from the placeholder
 * host, or the guard silently never fires and the operator gets 200 DNS failures.
 */
export const isDegradedBackend = (host: string): boolean =>
  host === UNKNOWN_HOST || host.includes('missing-tenant-config.invalid')

export function PhotoBulkUploadPage({
  processFile = browserProcessFile,
  backendUrl = ACTIVE_BACKEND_URL,
}: PhotoBulkUploadPageProps = {}) {
  const { message, modal } = App.useApp()
  // Feeds settleRun's post-batch cache invalidation (EC-56): Refine's
  // useInvalidate() satisfies the exported Invalidate type structurally, so no
  // cast is needed at the call site.
  const invalidate = useInvalidate()
  const { setWarnWhen } = useWarnAboutChange()

  const backendHost = React.useMemo(() => hostOf(backendUrl), [backendUrl])
  const badBackend = isDegradedBackend(backendHost)

  const [guard, setGuard] = React.useState<Guard>(badBackend ? 'bad-backend' : 'loading')
  const [uploadsEnabled, setUploadsEnabled] = React.useState<boolean>(true)
  const dedupeAvailable = React.useMemo(() => hasSubtleCrypto(), [])

  const [albumId, setAlbumId] = React.useState<string | undefined>(undefined)
  const [newAlbumMode, setNewAlbumMode] = React.useState(false)
  const [newAlbumName, setNewAlbumName] = React.useState('')
  const [albumNotice, setAlbumNotice] = React.useState<string | null>(null)
  const [labelWarning, setLabelWarning] = React.useState<string | null>(null)

  const [rows, setRows] = React.useState<BulkRow[]>([])
  const [phase, setPhase] = React.useState<Phase>('idle')
  const [pauseReason, setPauseReason] = React.useState<PauseReason | null>(null)
  const [abortMessage, setAbortMessage] = React.useState<string | null>(null)
  const [dropHint, setDropHint] = React.useState<string | null>(null)
  const [ignoredCount, setIgnoredCount] = React.useState(0)
  const [overflowNotice, setOverflowNotice] = React.useState<string | null>(null)
  const [announcement, setAnnouncement] = React.useState('')
  const [outcome, setOutcome] = React.useState<BatchOutcome | null>(null)
  /** The gallery's total photo count read right after settle, for the summary's row-cap line. */
  const [postBatchTotal, setPostBatchTotal] = React.useState<number | null>(null)
  /** True when the album that received this batch was created by this page. */
  const [albumCreated, setAlbumCreated] = React.useState(false)
  /** False when the max(created_at) clamp query failed (EC-34). */
  const [orderVerified, setOrderVerified] = React.useState(true)
  /** Global photo count, read once at mount, for the PostgREST row-cap warning. */
  const [galleryTotal, setGalleryTotal] = React.useState<number | null>(null)
  /** Drives the ">10s, still uploading" counters; one timer for the whole page. */
  const [nowMs, setNowMs] = React.useState(() => Date.now())
  /** The visible half of the Start re-entrancy guard; startingRef is the real one. */
  const [starting, setStarting] = React.useState(false)

  // rowsRef holds the MUTABLE rows the queue owns; React state holds snapshots,
  // so one row's transition re-renders one memoized row.
  const rowsRef = React.useRef<BulkRow[]>([])
  const queueRef = React.useRef<QueueHandle | null>(null)
  /**
   * What the live queue in queueRef was BUILT FOR. ONE queue per batch is a
   * correctness requirement, not a tidiness one: `orphanPaths` lives in the
   * queue's closure and is cumulative across start() calls on purpose
   * (uploadQueue.ts:204-208) because it is the only record of storage objects
   * nothing can list(). Building a second queue for "Retry failed" threw that
   * closure away, so a clean second pass reported a clean run over an object
   * that is still stranded - the exact thing EC-20 forbids. A queue may be
   * reused only while both of the things it captured at construction still
   * hold: the ROW ARRAY (it holds `rows` by reference, and addFiles replaces
   * that array) and the DESTINATION ALBUM (baked into its config, while the
   * album picker stays live after a terminal run).
   */
  const queueBatchRef = React.useRef<{ rows: BulkRow[]; albumId: string; uploadedBy: string } | null>(null)
  /**
   * I3. The orphan record ABOVE the queue, for the whole page session.
   *
   * `orphanPaths` is cumulative inside ONE queue closure, and the queue is
   * rebuilt by the two most ordinary things an operator does next: adding the
   * rest of an over-cap folder (a new row array) and changing the album. Either
   * threw the record away, and the next clean pass then reported a clean run
   * over an object nothing can list() - exactly what EC-20 forbids. This ref is
   * the union of every settled pass's orphanPaths since mount, de-duplicated,
   * and it is what the summary renders.
   */
  const sessionOrphansRef = React.useRef<string[]>([])
  const mountedRef = React.useRef(true)
  /** Set by onPause; read synchronously when the run settles (see runQueue). */
  const pausedRef = React.useRef(false)
  /**
   * Set SYNCHRONOUSLY at the top of onStart, before its first await. `phase` is
   * a render value and setPhase('running') does not land until deep inside
   * runBatch - after the confirm modal, getSession, the album resolution and two
   * queries - so a second press in that window would pass a phase-based guard
   * and build a SECOND queue over the same row objects. The queue's own
   * re-entrancy guard is per instance and cannot see the first one.
   */
  const startingRef = React.useRef(false)
  /** Mirrors phase for addFiles, which is dependency-free by design (NIT 7). */
  const intakeLockedRef = React.useRef(false)
  const filesInputRef = React.useRef<HTMLInputElement | null>(null)
  const folderInputRef = React.useRef<HTMLInputElement | null>(null)
  /**
   * `webkitdirectory` is a REF CALLBACK, not an effect, because the input does
   * not exist on the first commit: the whole "Add files" card is behind
   * `guard === 'admin'`, and `guard` starts at 'loading'. An effect with `[]`
   * therefore ran against a null ref, both setAttribute calls were
   * optional-chained no-ops, and it never re-ran - so "Choose folder" opened the
   * ordinary multi-file picker, identical to its neighbour, and no folder path
   * worked at all. A callback ref fires on whichever commit mounts the element,
   * whatever the guard did in between. React does not have props for either
   * attribute, so setAttribute is the only route.
   */
  const attachFolderInput = React.useCallback((el: HTMLInputElement | null) => {
    folderInputRef.current = el
    el?.setAttribute('webkitdirectory', '')
    el?.setAttribute('directory', '')
  }, [])

  const { selectProps: albumSelectProps } = useSelect<Album>({
    resource: 'albums',
    optionLabel: 'name',
    optionValue: 'id',
    pagination: { mode: 'off' },
  })

  // ---- guards ---------------------------------------------------------------
  React.useEffect(() => {
    if (badBackend) return
    let alive = true
    void (async () => {
      // Fail CLOSED: any failure of this check renders the not-admin state.
      try {
        const { data } = await supabaseClient.auth.getSession()
        const uid = data.session?.user?.id
        if (!uid) {
          if (alive) setGuard('not-admin')
          return
        }
        const res = await supabaseClient.from('profiles').select('role').eq('user_id', uid).maybeSingle()
        const role = (res.data as { role?: string } | null)?.role
        if (alive) setGuard(!res.error && role === 'admin' ? 'admin' : 'not-admin')
      } catch {
        if (alive) setGuard('not-admin')
      }
    })()
    return () => {
      alive = false
    }
  }, [badBackend])

  React.useEffect(() => {
    if (badBackend) return
    let alive = true
    void (async () => {
      try {
        const res = await supabaseClient
          .from('feature_flags')
          .select('enabled')
          .eq('key', 'uploads_enabled')
          .maybeSingle()
        const row = res.data as { enabled?: boolean } | null
        // Absent row means enabled, matching public.uploads_enabled().
        if (alive) setUploadsEnabled(res.error ? true : (row?.enabled ?? true))
      } catch {
        if (alive) setUploadsEnabled(true)
      }
    })()
    return () => {
      alive = false
    }
  }, [badBackend])

  // The gallery's GLOBAL photo count, read once. mobile usePhotos() with no
  // albumId selects EVERY photo ascending with no limit, so PostgREST's
  // max_rows = 1000 applies to the whole table, not to one album.
  React.useEffect(() => {
    if (badBackend) return
    let alive = true
    void (async () => {
      try {
        const res = await supabaseClient.from('photos').select('id', { count: 'exact', head: true })
        if (alive) setGalleryTotal(res.error ? null : (res.count ?? null))
      } catch {
        if (alive) setGalleryTotal(null)
      }
    })()
    return () => {
      alive = false
    }
  }, [badBackend])

  // mountedRef MUST be re-armed in the effect body, not only cleared in the
  // cleanup: admin/src/main.tsx renders inside <React.StrictMode>, which in dev
  // runs mount -> cleanup -> mount. Without the re-arm the second mount starts
  // with mountedRef.current === false and every async continuation is dropped,
  // so the page silently never updates. `setWarnWhen` is the stable setState
  // from Refine's UnsavedWarnContextProvider, so this effect runs exactly twice
  // in dev and once in prod; if it ever started re-running it would revoke
  // object URLs that are still on screen.
  //
  // CAVEAT, deferred by ruling: that stability is a Refine internal. Its
  // UnsavedWarnContext defaults to `{}` and useWarnAboutChange() returns
  // `setWarnWhen ?? (() => void 0)`, so OUTSIDE a <Refine> provider the fallback
  // is a fresh closure every render, this effect re-runs, and its cleanup
  // revokes live previews mid-batch. Unreachable today - the page always renders
  // inside <Refine>, and so does the test harness - so the dependency stays as
  // written rather than being papered over with a ref.
  React.useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      setWarnWhen(false)
      for (const row of rowsRef.current) {
        if (row.previewUrl) URL.revokeObjectURL(row.previewUrl)
      }
    }
  }, [setWarnWhen])

  // One timer drives both the elapsed hints and the throttled announcement.
  React.useEffect(() => {
    if (phase !== 'running') return undefined
    let ticks = 0
    const timer = window.setInterval(() => {
      ticks += 1
      setNowMs(Date.now())
      if (ticks % 5 === 0) {
        const done = rowsRef.current.filter((r) => r.state === 'done').length
        setAnnouncement(`${done} of ${rowsRef.current.length} complete`)
      }
    }, 1000)
    return () => window.clearInterval(timer)
  }, [phase])

  // ---- file intake ----------------------------------------------------------
  const addFiles = React.useCallback((incoming: File[]) => {
    // The invariant belongs to the function that would break it, not only to its
    // callers: the queue owns rowsRef.current BY REFERENCE, and appending
    // replaces the array out from under a running or paused batch.
    if (intakeLockedRef.current) return
    if (incoming.length === 0) {
      setDropHint(COPY.emptyDrop)
      return
    }
    const part = partitionDroppedFiles(incoming, rowsRef.current.length)
    setIgnoredCount((n) => n + part.ignoredCount)
    setOverflowNotice(
      part.overflowCount > 0
        ? `Added ${part.accepted.length} of ${incoming.length} files (batch limit ${MAX_FILES}). Upload these, then add the rest.`
        : null,
    )
    setDropHint(part.accepted.length === 0 && part.rejected.length === 0 ? COPY.emptyDrop : null)

    // No casts: partitionDroppedFiles is generic, so File[] in gives File[] back.
    const accepted = part.accepted.map((f) => makeRow(f))
    const rejected = part.rejected.map(({ file, reason }) => ({
      ...makeRow(file),
      state: 'failed' as const,
      reason,
    }))
    rowsRef.current = [...rowsRef.current, ...accepted, ...rejected]
    setRows([...rowsRef.current])
    setOutcome(null)
  }, [])

  const onPickFiles = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      addFiles(Array.from(e.target.files ?? []))
      e.target.value = ''
    },
    [addFiles],
  )

  const onLabelChange = React.useCallback((id: string, label: string) => {
    const target = rowsRef.current.find((r) => r.id === id)
    if (target) target.label = label
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, label } : r)))
  }, [])

  const onKeepAnyway = React.useCallback((id: string) => {
    queueRef.current?.keepAnyway(id)
    const target = rowsRef.current.find((r) => r.id === id)
    if (target && target.state === 'skipped') {
      target.state = 'queued'
      target.keepAnyway = true
      target.reason = undefined
    }
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, state: 'queued' as const, keepAnyway: true, reason: undefined } : r)),
    )
  }, [])

  const syncRow = React.useCallback((id: string, patch: Partial<BulkRow>) => {
    if (!mountedRef.current) return
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  }, [])

  // ---- start ---------------------------------------------------------------
  const startable = rows.filter((r) => r.state === 'queued' || r.state === 'failed' || r.state === 'cancelled').length
  const doneCount = rows.filter((r) => r.state === 'done').length
  /**
   * After a permission abort, `start()` clears its OWN `aborted` flag on the
   * next call - the queue has no memory that the operator's admin role was the
   * problem. So re-running through the same button would just re-attempt the
   * still-queued rows and immediately re-abort on the next 42501, one row at a
   * time, forever. The button is therefore gated on the abort reason: a
   * permission abort never becomes "Retry failed", and it stays disabled
   * (spec note carried over from the Task 4 review, not the literal Task 6
   * brief text).
   */
  const permissionAborted = abortMessage === ABORT_PERMISSION_COPY
  /** After a terminal run with work left, the same control becomes Retry (spec 6.5). */
  const isRetry = phase === 'terminal' && startable > 0 && !permissionAborted
  /**
   * The queue owns `rowsRef.current` by REFERENCE from the moment it is created.
   * Adding files replaces that array, so anything the operator adds while a batch
   * is running OR paused would be invisible to the queue that Resume restarts.
   * Intake is therefore closed for both phases; the operator adds the rest after
   * the batch settles, which is what the overflow copy already tells them to do.
   */
  const intakeLocked = phase === 'running' || phase === 'paused'
  intakeLockedRef.current = intakeLocked
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false
  const hasAlbum = newAlbumMode ? newAlbumName.trim().length > 0 : !!albumId
  const disabledReason = permissionAborted
    ? abortMessage
    : !hasAlbum
      ? COPY.needAlbum
      : startable === 0
        ? COPY.needFiles
        : offline
          ? COPY.offline
          : null

  /** Refine's options are BaseOption ({ label?: any; value?: any }); flatten to strings once. */
  const albumOptions = React.useMemo(
    () =>
      (albumSelectProps.options ?? []).map((o) => ({
        value: String(o.value ?? ''),
        label: String(o.label ?? ''),
      })),
    [albumSelectProps.options],
  )

  const albumDisplayName = React.useMemo(() => {
    if (newAlbumMode) return newAlbumName.trim()
    return albumOptions.find((o) => o.value === String(albumId))?.label ?? ''
  }, [albumId, albumOptions, newAlbumMode, newAlbumName])

  const albumDeps = React.useMemo<AlbumDeps>(
    () => ({
      // album.ts's resolveAlbumForStart awaits these with no try/catch of its
      // own, so a thrown error (a rejected fetch, an aborted request) must be
      // converted to the { id: null, error } shape here or it propagates
      // uncaught out of onStart.
      createAlbum: async (name) => {
        try {
          const { data, error } = await supabaseClient.from('albums').insert({ name }).select('id').single()
          return { id: (data as { id?: string } | null)?.id ?? null, error }
        } catch (error) {
          return { id: null, error }
        }
      },
      findAlbumIdByName: async (name) => {
        try {
          // ATKF3-R2 (final-review round 3): `albums.name` is unique only
          // WITHIN each of two families now (plain vs. group albums — see
          // 20261018120800_groups_audience_fixes_round5.sql), so a name can
          // exist once in each. `createAlbum` above only ever inserts a
          // PLAIN album (no `group_id`), so the 23505 that leads here can
          // only be a collision with another plain album — scope the lookup
          // to `group_id is null` or an admin (who can see every album,
          // Groups included) could match a same-named GROUP album too and
          // get a multi-row error back instead of the one row that's
          // actually taken.
          const { data, error } = await supabaseClient
            .from('albums')
            .select('id')
            .eq('name', name)
            .is('group_id', null)
            .maybeSingle()
          return { id: (data as { id?: string } | null)?.id ?? null, error }
        } catch (error) {
          return { id: null, error }
        }
      },
    }),
    [],
  )

  const makeDeps = React.useCallback(
    (): QueueDeps => ({
      uploadObject: async (path, body, options) => {
        const { error } = await supabaseClient.storage.from('photos').upload(path, body, options)
        return { error }
      },
      getPublicUrl: (path) => supabaseClient.storage.from('photos').getPublicUrl(path).data.publicUrl,
      insertPhoto: async (row) => {
        const { data, error } = await supabaseClient.from('photos').insert(row).select('id').single()
        return { id: (data as { id?: string } | null)?.id ?? null, error }
      },
      removeObject: async (path) => {
        const { error } = await supabaseClient.storage.from('photos').remove([path])
        return { error }
      },
      process: processFile,
      now: () => Date.now(),
      storageOrigin: backendUrl,
    }),
    [backendUrl, processFile],
  )

  /**
   * The bookkeeping a settled run does, shared by Start and Resume.
   *
   * A PAUSE IS NOT THE END OF THE BATCH. queue.start() resolves when the workers
   * stop, and a pause stops them with rows still outstanding — so disarming
   * `warnWhen` here unconditionally (e.g. in a `finally`) would let an operator
   * navigate away from a half-finished batch with no prompt. Only a terminal run
   * disarms it.
   */
  const settleRun = React.useCallback(
    (result: BatchOutcome, albumWasCreated: boolean) => {
      if (!mountedRef.current) return
      // I3. Fold this pass's orphans into the session ledger and render the
      // UNION, so a queue rebuilt between passes can never let a later summary
      // claim a clean run over a stranded object. No double counting: a reused
      // queue's own orphanPaths are already cumulative, and a Set is taken.
      const orphanPaths = [...new Set([...sessionOrphansRef.current, ...result.orphanPaths])]
      sessionOrphansRef.current = orphanPaths
      setOutcome({ ...result, orphanPaths })
      // M2. STICKY: pass 2 resolves a created album as { kind: 'existing' }, so a
      // plain assignment cleared the "created and is empty" cleanup hint on the
      // retry that makes it most relevant. runInvalidations still reads the
      // parameter, so the `albums` invalidation fires only on the creating pass.
      setAlbumCreated((prev) => prev || albumWasCreated)
      const stillPaused = pausedRef.current
      setPhase(stillPaused ? 'paused' : 'terminal')
      if (stillPaused) return

      setWarnWhen(false)
      // EC-61's final announcement. Terminal only: a pause leaves rows
      // outstanding, and announcing a tally there would claim the batch ended.
      // The 5 s ticker's effect is torn down by the same phase change, so
      // nothing can overwrite this afterwards.
      setAnnouncement(COPY.finished(result.done, result.failed, result.skipped))

      // Refine caches lists for 5 minutes with liveMode 'off'. Without this the
      // Photos list and the stat deck still show pre-batch counts and the
      // operator uploads the same folder again.
      runInvalidations(invalidate, { albumCreated: albumWasCreated })

      void (async () => {
        try {
          const countRes = await supabaseClient.from('photos').select('id', { count: 'exact', head: true })
          if (mountedRef.current) setPostBatchTotal(countRes.error ? null : (countRes.count ?? null))
        } catch {
          if (mountedRef.current) setPostBatchTotal(null)
        }
      })()
    },
    [invalidate, setWarnWhen],
  )

  const runBatch = React.useCallback(
    async (resolvedAlbumId: string, uid: string, albumWasCreated: boolean) => {
      let latest: number | null = null
      let verified = true
      try {
        const res = await supabaseClient
          .from('photos')
          .select('created_at')
          .eq('album_id', resolvedAlbumId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
        if (res.error) throw res.error
        const row = res.data as { created_at?: string } | null
        latest = row?.created_at ? Date.parse(row.created_at) : null
      } catch {
        // EC-34: without max(created_at) the clamp is unverified, so the summary
        // must say so rather than implying the order was checked.
        latest = null
        verified = false
      }
      setOrderVerified(verified)

      // The ONE label path with no validation is the one the operator controls:
      // the row input is uncontrolled (defaultValue), so clamping per keystroke
      // would fight the caret. Normalize once, here, so a typed label obeys the
      // same contract sanitizeLabel gives every derived one before the queue
      // reads row.label for the insert.
      for (const row of rowsRef.current) row.label = clampLabel(row.label)
      setRows((prev) => prev.map((r) => ({ ...r, label: clampLabel(r.label) })))

      // See queueBatchRef: "Retry failed" re-runs start() on the SAME handle so
      // the batch's cumulative orphan record survives the pass. A new batch is
      // the operator adding files (a new row array) or choosing a different
      // album; either resets everything the old queue was holding.
      const batch = queueBatchRef.current
      const reused =
        queueRef.current && batch && batch.rows === rowsRef.current && batch.albumId === resolvedAlbumId
          ? queueRef.current
          : null

      const queue =
        reused ??
        createUploadQueue(rowsRef.current, makeDeps(), {
          albumId: resolvedAlbumId,
          uploadedBy: uid,
          batchStartMs: clampBatchStart(Date.now(), latest),
          onRow: syncRow,
          onPause: (reason) => {
            pausedRef.current = true
            if (!mountedRef.current) return
            setPauseReason(reason)
            setPhase('paused')
          },
          onAbort: (reasonMessage) => {
            if (!mountedRef.current) return
            setAbortMessage(reasonMessage)
          },
        })
      queueRef.current = queue
      queueBatchRef.current = { rows: rowsRef.current, albumId: resolvedAlbumId, uploadedBy: uid }

      pausedRef.current = false
      setWarnWhen(true)
      setPhase('running')
      // Belongs to the pass that just ended, not to the one starting: without
      // this the new summary shows the previous pass's row-cap number for the
      // round-trip it takes the count query to answer.
      setPostBatchTotal(null)
      setPauseReason(null)
      setAbortMessage(null)
      settleRun(await queue.start(), albumWasCreated)
    },
    [makeDeps, setWarnWhen, settleRun, syncRow],
  )

  const onStart = React.useCallback(async () => {
    // startingRef is the guard that actually closes the double-start hole; the
    // `phase` test below is a render value and is stale for the whole window
    // between the press and setPhase('running'). See startingRef's declaration.
    if (disabledReason || phase === 'running' || startingRef.current) return
    startingRef.current = true
    setStarting(true)
    try {
      const confirmed = await new Promise<boolean>((resolve) => {
        modal.confirm({
          title: 'Confirm upload',
          content: `Upload ${startable} photo${startable === 1 ? '' : 's'} to "${albumDisplayName}" on ${backendHost}`,
          okText: 'Upload',
          cancelText: 'Cancel',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })
      if (!confirmed) return

      // uploaded_by is read ONCE, here - never from a stale identity render.
      // getSession CAN throw (navigator-lock and storage-access failures are the
      // field cases), and this handler is fired as `void onStart()`, so an
      // unguarded throw is an unhandled rejection: no toast, no phase change,
      // and a button that still looks ready.
      let uid: string | undefined
      try {
        const { data: sessionData } = await supabaseClient.auth.getSession()
        uid = sessionData.session?.user?.id
      } catch {
        uid = undefined
      }
      if (!uid) {
        message.error(COPY.noSession)
        return
      }

      const resolved = await resolveAlbumForStart(
        newAlbumMode ? { kind: 'new', name: newAlbumName } : { kind: 'existing', id: String(albumId) },
        albumDeps,
      )
      if (!resolved.ok) {
        message.error(resolved.reason)
        return
      }
      if (resolved.notice) {
        setAlbumNotice(resolved.notice)
        setNewAlbumMode(false)
        setAlbumId(resolved.albumId)
      }

      try {
        const res = await supabaseClient.from('photos').select('label').eq('album_id', resolved.albumId).limit(1000)
        const labels = ((res.data ?? []) as Array<{ label?: string }>).map((r) => r.label ?? '')
        setLabelWarning(existingLabelWarning(labels, rowsRef.current.map((r) => r.label)))
      } catch {
        setLabelWarning(null)
      }

      await runBatch(resolved.albumId, uid, resolved.created)
    } finally {
      startingRef.current = false
      if (mountedRef.current) setStarting(false)
    }
  }, [
    albumDeps,
    albumDisplayName,
    albumId,
    backendHost,
    disabledReason,
    message,
    modal,
    newAlbumMode,
    newAlbumName,
    phase,
    runBatch,
    startable,
  ])

  const onCancel = React.useCallback(() => {
    queueRef.current?.cancel()
  }, [])

  const onResume = React.useCallback(async () => {
    const queue = queueRef.current
    if (!queue) return

    // EC-43: the pause is very often the expiry itself, and start() clears the
    // queue's own paused flag - so resuming without re-checking would re-run the
    // batch against a dead token, one 401 at a time. Worse after a re-auth as a
    // DIFFERENT admin: uploadedBy was captured once at Start, and nothing
    // server-side forces uploaded_by to match auth.uid() (EC-68). Handled
    // locally: no navigation, and never through Refine's error path.
    let uid: string | undefined
    try {
      const { data } = await supabaseClient.auth.getSession()
      uid = data.session?.user?.id
    } catch {
      uid = undefined
    }
    if (!uid) {
      // Stay paused. The queue, the rows and warnWhen are all untouched, so a
      // Resume after a real sign-in still continues from the first non-done row.
      setPauseReason({ kind: 'auth', message: PAUSE_AUTH_COPY })
      message.error(PAUSE_AUTH_COPY)
      return
    }
    // M4. The uid is re-checked, so USE it. The queue's uploadedBy was captured
    // once at Start; resuming after signing back in as a DIFFERENT admin would
    // attribute the rest of the batch to the previous one in photos.uploaded_by,
    // and nothing server-side forces the two to agree (EC-68). Audit attribution
    // is unaffected (admin_audit_log.actor_id comes from auth.uid() per row), so
    // the cheap honest answer is to stay paused rather than write wrong data.
    if (queueBatchRef.current && uid !== queueBatchRef.current.uploadedBy) {
      message.error(COPY.otherAdmin)
      return
    }

    // I2. An album-gone pause is only recoverable if the queue is re-pointed:
    // its albumId feeds both buildStoragePath and the INSERT payload, so a plain
    // resume re-targets the deleted album, re-fails 23503 and re-pauses - and
    // Start is disabled for the whole of `paused`, leaving only a reload, which
    // discards the queue the pause copy promises to keep. Resolve the picker's
    // CURRENT selection exactly as Start does (existing album or a new one), then
    // hand it to the live queue before restarting it.
    let albumWasCreated = albumCreated
    if (pauseReason?.kind === 'album-gone') {
      const resolved = await resolveAlbumForStart(
        newAlbumMode ? { kind: 'new', name: newAlbumName } : { kind: 'existing', id: String(albumId) },
        albumDeps,
      )
      if (!resolved.ok) {
        message.error(resolved.reason)
        return
      }
      if (resolved.notice) {
        setAlbumNotice(resolved.notice)
        setNewAlbumMode(false)
        setAlbumId(resolved.albumId)
      }
      queue.setAlbumId(resolved.albumId)
      // The reuse key travels with the queue, or the next "Retry failed" into
      // this album would build a second one over the same rows.
      if (queueBatchRef.current) queueBatchRef.current.albumId = resolved.albumId
      // The batch's created_at clamp was computed against the OLD album, so the
      // batch's position relative to THIS album's existing photos genuinely was
      // not verified. EC-34's line says exactly that.
      setOrderVerified(false)
      // Not sticky here: the destination changed, so a stale "was created and is
      // empty" line would name the album the operator just switched TO.
      albumWasCreated = resolved.created
      setAlbumCreated(resolved.created)
    }

    queue.resume()
    pausedRef.current = false
    setPauseReason(null)
    setPhase('running')
    setPostBatchTotal(null)
    setWarnWhen(true)
    // start() cannot reject today (uploadQueue's .catch().finally().then()), but
    // this is the one `void ... .then()` on the page, so it gets the catch.
    void queue
      .start()
      .then((result) => settleRun(result, albumWasCreated))
      .catch(() => undefined)
  }, [
    albumCreated,
    albumDeps,
    albumId,
    message,
    newAlbumMode,
    newAlbumName,
    pauseReason,
    setWarnWhen,
    settleRun,
  ])

  // ---- render ---------------------------------------------------------------
  if (guard === 'bad-backend') {
    return (
      <ListPage intro={{ eyebrow: COPY.eyebrow, description: COPY.intro }}>
        <div data-testid="muaa-bulk-upload-page">
          <SectionCard title="Destination">
            <EmptyHint>{COPY.badConfig}</EmptyHint>
          </SectionCard>
        </div>
      </ListPage>
    )
  }

  // EC-32: the ceiling is on the GALLERY, not on the batch. mobile usePhotos()
  // with no albumId selects every photo ascending with no limit, so PostgREST's
  // max_rows = 1000 truncates the NEWEST rows once the whole table passes 1000.
  // galleryTotal is null when the count query failed — then say nothing rather
  // than warning off a number that is not real.
  const galleryTotalHint = galleryTotal == null ? null : thresholdWarning(galleryTotal + startable)

  /**
   * EC-71's second half. Derived from the rows the same way the queue derives
   * `outcome.photoIds` (state === 'done' && photoId), so the two can never
   * disagree, and paired with `row.path` because a mis-targeted batch is undone
   * by deleting the photo row AND the object it left behind. A plain
   * derivation, not a useMemo: this sits after the bad-backend early return,
   * where a hook would be conditional.
   */
  const photoRecords = rows.flatMap((r) =>
    r.state === 'done' && r.photoId ? [{ id: r.photoId, path: r.path }] : [],
  )

  return (
    <ListPage
      intro={{
        eyebrow: COPY.eyebrow,
        description: COPY.intro,
        actions: (
          <Link to="/photos">
            <Button>{COPY.back}</Button>
          </Link>
        ),
      }}
    >
      <div data-testid="muaa-bulk-upload-page" style={{ display: 'grid', gap: 16 }}>
        <div
          data-testid="muaa-bulk-upload-live"
          aria-live="polite"
          style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
        >
          {announcement}
        </div>

        <SectionCard title="Destination">
          {guard === 'loading' ? (
            <Spin />
          ) : guard === 'not-admin' ? (
            <EmptyHint>{COPY.notAdmin}</EmptyHint>
          ) : (
            <div style={{ display: 'grid', gap: 10, maxWidth: 640 }}>
              {/* EC-46: the host STRING alone is easy to skim past. The badge is
                  the same one the header carries, driven by the same URL, so a
                  DEV batch and a LIVE batch never look alike. */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  fontFamily: brand.fSans,
                  fontSize: 12,
                  color: brand.sub,
                }}
              >
                <span>Uploading to: {backendHost}</span>
                <EnvBadge url={backendUrl} />
              </div>
              <label style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.ink }} htmlFor="bulk-album">
                {COPY.albumLabel}
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span data-testid="muaa-bulk-upload-album-select" style={{ flex: '1 1 260px' }}>
                  {/* Do NOT spread albumSelectProps here. It is typed
                      SelectProps<BaseOption>, so `value={albumId}` (a string)
                      does not typecheck against it. `pagination: { mode: 'off' }`
                      already fetched every album, so the options array is all we
                      need and the filtering is local. */}
                  <Select<string>
                    id="bulk-album"
                    aria-label={COPY.albumLabel}
                    placeholder={COPY.albumPlaceholder}
                    showSearch
                    optionFilterProp="label"
                    options={albumOptions}
                    loading={albumSelectProps.loading}
                    value={albumId}
                    onChange={(v) => {
                      setAlbumId(v)
                      setNewAlbumMode(false)
                    }}
                    /* `running` only, NOT `paused`: an album-gone pause tells the
                       operator to pick another album, and onResume re-resolves
                       whatever is selected here and re-points the live queue. */
                    disabled={phase === 'running'}
                    style={{ width: '100%' }}
                  />
                </span>
                <Button onClick={() => setNewAlbumMode((v) => !v)} disabled={phase === 'running'}>
                  {COPY.newAlbum}
                </Button>
              </div>
              {newAlbumMode ? (
                <Input
                  data-testid="muaa-bulk-upload-new-album-input"
                  aria-label={COPY.newAlbum}
                  placeholder={COPY.newAlbumPlaceholder}
                  value={newAlbumName}
                  onChange={(e) => setNewAlbumName(e.target.value)}
                  disabled={phase === 'running'}
                />
              ) : null}
              {albumNotice ? <Pill tone="gold">{albumNotice}</Pill> : null}
              {labelWarning ? <Pill tone="gold">{labelWarning}</Pill> : null}
              {!uploadsEnabled ? <Pill tone="gold">{COPY.killSwitch}</Pill> : null}
            </div>
          )}
        </SectionCard>

        {guard === 'admin' ? (
          <SectionCard title="Add files">
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.ink }}>{COPY.moderation}</div>
              <div
                data-testid="muaa-bulk-upload-dropzone"
                /* aria-label on a role-less <div> is ignored by most assistive
                   tech, so the spec's label needs a role to attach to. */
                role="group"
                aria-label="Drop photos or a folder to upload"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  if (intakeLocked) return
                  addFiles(Array.from(e.dataTransfer?.files ?? []))
                }}
                style={{
                  border: '1px dashed ' + brand.line,
                  borderRadius: 12,
                  padding: 22,
                  textAlign: 'center',
                  background: brand.paper,
                }}
              >
                <div style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 13, color: brand.ink }}>
                  {COPY.draggerPrimary}
                </div>
                <div style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub, marginTop: 4 }}>
                  {COPY.draggerSecondary}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  data-testid="muaa-bulk-upload-choose-files"
                  onClick={() => filesInputRef.current?.click()}
                  disabled={intakeLocked}
                >
                  {COPY.chooseFiles}
                </Button>
                <Button
                  data-testid="muaa-bulk-upload-choose-folder"
                  onClick={() => folderInputRef.current?.click()}
                  disabled={intakeLocked}
                >
                  {COPY.chooseFolder}
                </Button>
                <input
                  ref={filesInputRef}
                  type="file"
                  multiple
                  accept={ACCEPT_ATTR}
                  onChange={onPickFiles}
                  style={{ display: 'none' }}
                  aria-hidden="true"
                  tabIndex={-1}
                />
                <input
                  ref={attachFolderInput}
                  data-testid="muaa-bulk-upload-folder-input"
                  type="file"
                  multiple
                  onChange={onPickFiles}
                  style={{ display: 'none' }}
                  aria-hidden="true"
                  tabIndex={-1}
                />
              </div>

              <div style={{ fontFamily: brand.fSans, fontSize: 11.5, color: brand.sub, display: 'grid', gap: 4 }}>
                <span>{dedupeAvailable ? COPY.dedupeScope : COPY.dedupeUnavailable}</span>
                <span>{COPY.auditNote}</span>
                <span>{COPY.interleave}</span>
                <span>{COPY.noResume}</span>
                {ignoredCount > 0 ? <span>{ignoredCount} system files ignored.</span> : null}
                {overflowNotice ? <span>{overflowNotice}</span> : null}
                {galleryTotalHint ? <span>{galleryTotalHint}</span> : null}
                {dropHint ? (
                  <>
                    <span>{dropHint}</span>
                    <span>{COPY.emptyDropSecond}</span>
                  </>
                ) : null}
              </div>
            </div>
          </SectionCard>
        ) : null}

        {guard === 'admin' ? (
          <SectionCard title={`Files (${rows.length})`}>
            {/* The controls are OUTSIDE the empty branch on purpose: spec 6.2's
                `idle` state requires Start to be visible and disabled with an
                inline reason before any file exists (EC-58). Hiding it until a
                file is added leaves the operator with nothing to read. */}
            <div style={{ display: 'grid', gap: 12 }}>
              {rows.length === 0 ? <EmptyHint>{COPY.noFiles}</EmptyHint> : null}

              {rows.length > 0 ? (
                <div data-testid="muaa-bulk-upload-progress">
                  <ProgressBar value={doneCount} max={rows.length} />
                  <div style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub, marginTop: 6 }}>
                    {doneCount} of {rows.length} complete
                  </div>
                </div>
              ) : null}

              {pauseReason ? <Pill tone="gold">{pauseReason.message}</Pill> : null}
              {abortMessage ? <Pill tone="red">{abortMessage}</Pill> : null}
              {phase === 'running' ? <Pill tone="jaguar">{COPY.concurrency}</Pill> : null}

              {rows.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  {rows.map((row) => (
                    <BulkUploadRow
                      key={row.id}
                      id={row.id}
                      name={row.name}
                      size={row.size}
                      label={row.label}
                      state={row.state}
                      reason={row.reason}
                      previewUrl={row.previewUrl}
                      metadataRetained={row.metadataRetained}
                      /* NOT `asIs && metadataRetained`: that is true for all three
                         as-is causes, so a GIF - which is never compared against
                         anything - claimed a size comparison that never ran. */
                      keptOriginal={row.keptOriginal === true}
                      elapsedS={
                        row.state === 'uploading' && row.startedAtMs
                          ? Math.max(0, Math.round((nowMs - row.startedAtMs) / 1000))
                          : 0
                      }
                      onLabelChange={onLabelChange}
                      onKeepAnyway={onKeepAnyway}
                    />
                  ))}
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  /* Spec 6.5 lists BOTH ids; the control is one button whose
                     identity changes with its job, so the testID follows. */
                  data-testid={isRetry ? 'muaa-bulk-upload-retry' : 'muaa-bulk-upload-start'}
                  type="primary"
                  /* `starting` and `phase === 'paused'` are the two additions to
                     the original pair: the first is the visible half of the
                     re-entrancy guard, the second leaves Resume as the only way
                     forward from a pause (Start would build a fresh queue with a
                     new batchStartMs over the same rows). */
                  disabled={!!disabledReason || phase === 'running' || phase === 'paused' || starting}
                  onClick={() => void onStart()}
                >
                  {isRetry ? `Retry failed (${startable})` : COPY.start}
                </Button>
                {phase === 'running' ? (
                  <Button data-testid="muaa-bulk-upload-cancel" onClick={onCancel}>
                    {COPY.cancel}
                  </Button>
                ) : null}
                {phase === 'paused' ? (
                  <Button data-testid="muaa-bulk-upload-resume" onClick={() => void onResume()}>
                    {COPY.resume}
                  </Button>
                ) : null}
                {disabledReason ? (
                  <span style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub }}>{disabledReason}</span>
                ) : null}
              </div>
            </div>
          </SectionCard>
        ) : null}

        {/* Spec 6.1: the Summary card is rendered ONLY after a run reaches a
            terminal state. `outcome` alone is not that test - settleRun writes
            it on a PAUSE too (deliberately, so the card is already correct when
            the resumed pass settles), and it survives into the next pass. Ungated,
            the card told a paused operator the batch had "finished" and offered
            to delete the album it was about to resume into, and it described a
            running retry with the previous pass's counts. `terminal` covers
            complete, partial, cancelled and the permission abort alike. */}
        {phase === 'terminal' && outcome ? (
          <BulkUploadSummary
            outcome={outcome}
            albumName={albumDisplayName}
            totalRows={rows.length}
            albumCreatedEmpty={albumCreated && outcome.done === 0}
            postBatchTotal={postBatchTotal}
            orderVerified={orderVerified}
            photoRecords={photoRecords}
          />
        ) : null}
      </div>
    </ListPage>
  )
}
